// ===========================================
// Record / Amend Registration Payment Dialog
// ===========================================
// Records the family's one-off registration fee as paid another way (offline),
// or amends a manual row already on record. md/plan/family-payment-history.md
// Task 4.3.

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { useRecordRegistrationPayment, useUpdateRegistrationPayment } from '../../../hooks/useFamilyPayments';
import type { FamilyPaymentRow } from '../../../api/familyPayments.api';
import type { PaymentMethod } from '../../../api/invoices.api';
import { useSchoolTimezone } from '../../../contexts/TenantContext';
import { schoolTodayUtcMidnight, toUTCDateKey } from '../../../utils/dateUtils';
import { apiErrorCode, apiErrorMessage } from '../../../utils/apiError';
import RegistrationFeeFields, {
  RegistrationFeeFormValues,
  isRegistrationFeeValid,
} from './RegistrationFeeFields';

interface RecordRegistrationPaymentDialogProps {
  open: boolean;
  onClose: () => void;
  familyId: string | undefined;
  /** Prefilled amount for a new record, e.g. "50.00". */
  defaultAmount: string;
  /** When set, the dialog amends this (manual) payment instead of recording a new one. */
  payment?: FamilyPaymentRow | null;
  /** #374: the family's active children. With 2+ the dialog asks which one the fee is for. */
  childOptions?: { id: string; name: string }[];
  /** #374 PER_STUDENT: with exactly one listed child, send it explicitly (a sibling already has a fee). */
  sendSoleChild?: boolean;
}

const emptyValues = (todayKey: string, defaultAmount: string): RegistrationFeeFormValues => ({
  amount: defaultAmount,
  method: 'BANK_TRANSFER',
  paidAt: todayKey,
  reference: '',
  notes: '',
});

export default function RecordRegistrationPaymentDialog({
  open,
  onClose,
  familyId,
  defaultAmount,
  payment = null,
  childOptions = [],
  sendSoleChild = false,
}: RecordRegistrationPaymentDialogProps) {
  const timeZone = useSchoolTimezone();
  const todayKey = toUTCDateKey(schoolTodayUtcMidnight(timeZone));

  // Keep the payment being amended in LOCAL state so the dialog's mode/title
  // doesn't flip while the close transition is still playing — `payment` may
  // already have changed (e.g. to null) by the time the fade-out finishes.
  const [activePayment, setActivePayment] = useState<FamilyPaymentRow | null>(payment);
  const isEdit = activePayment !== null;
  const needsChild = !isEdit && childOptions.length > 1;

  const [values, setValues] = useState<RegistrationFeeFormValues>(emptyValues(todayKey, defaultAmount));
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [studentId, setStudentId] = useState('');
  const [studentError, setStudentError] = useState<string | null>(null);
  // Snapshot of the amend prefill, so Save can stay disabled until something
  // actually changed — re-submitting an unmodified amend is a no-op that
  // still hits the audit log.
  const [prefillValues, setPrefillValues] = useState<RegistrationFeeFormValues | null>(null);

  const recordMutation = useRecordRegistrationPayment();
  const updateMutation = useUpdateRegistrationPayment();
  const mutation = isEdit ? updateMutation : recordMutation;

  useEffect(() => {
    if (!open) return;
    setActivePayment(payment);
    setSubmitError(null);
    setStudentId('');
    setStudentError(null);
    if (payment) {
      const prefill: RegistrationFeeFormValues = {
        amount: parseFloat(payment.amount).toFixed(2),
        method: payment.method as Exclude<PaymentMethod, 'STRIPE'>,
        paidAt: toUTCDateKey(schoolTodayUtcMidnight(timeZone, new Date(payment.paidAt))),
        reference: payment.reference ?? '',
        notes: payment.notes ?? '',
      };
      setValues(prefill);
      setPrefillValues(prefill);
    } else {
      setValues(emptyValues(todayKey, defaultAmount));
      setPrefillValues(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, payment, defaultAmount, todayKey, timeZone]);

  const handleClose = () => {
    if (mutation.isPending) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (!familyId) return;
    setSubmitError(null);
    setStudentError(null);
    try {
      if (isEdit && activePayment) {
        await updateMutation.mutateAsync({
          id: activePayment.id,
          data: {
            amount: parseFloat(values.amount),
            method: values.method,
            // '' clears reference/notes on an amend
            reference: values.reference,
            notes: values.notes,
            paidAt: values.paidAt,
          },
        });
      } else {
        await recordMutation.mutateAsync({
          familyId,
          data: {
            amount: parseFloat(values.amount),
            method: values.method,
            reference: values.reference || undefined,
            notes: values.notes || undefined,
            paidAt: values.paidAt,
            // One child: the server picks it. 2+: the chosen one.
            ...(needsChild
              ? { studentId }
              : sendSoleChild && !isEdit && childOptions.length === 1
                ? { studentId: childOptions[0].id }
                : {}),
          },
        });
      }
      onClose();
    } catch (error) {
      if (apiErrorCode(error) === 'STUDENT_REQUIRED') {
        setStudentError('Choose the child this registration fee is for');
        return;
      }
      setSubmitError(
        apiErrorMessage(error, isEdit ? 'Failed to update registration payment' : 'Failed to record registration payment')
      );
    }
  };

  const valid = isRegistrationFeeValid(values, todayKey) && (!needsChild || studentId !== '');
  const isDirty =
    !isEdit ||
    !prefillValues ||
    values.amount !== prefillValues.amount ||
    values.method !== prefillValues.method ||
    values.paidAt !== prefillValues.paidAt ||
    values.reference !== prefillValues.reference ||
    values.notes !== prefillValues.notes;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{ onExited: () => setActivePayment(null) }}
    >
      <DialogTitle>{isEdit ? 'Edit Registration Payment' : 'Record Registration Payment'}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {needsChild && (
            <FormControl size="small" fullWidth required error={!!studentError} disabled={mutation.isPending}>
              <InputLabel id="record-fee-child-label">Child</InputLabel>
              <Select
                labelId="record-fee-child-label"
                label="Child"
                value={studentId}
                onChange={(e) => {
                  setStudentId(e.target.value as string);
                  setStudentError(null);
                }}
              >
                {childOptions.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
              {studentError && <FormHelperText>{studentError}</FormHelperText>}
            </FormControl>
          )}

          <RegistrationFeeFields
            values={values}
            onChange={setValues}
            todayKey={todayKey}
            disabled={mutation.isPending}
          />

          {isEdit && (
            <Typography variant="caption" color="text.secondary">
              Saving corrects this record; no email is sent.
            </Typography>
          )}

          {submitError && <Alert severity="error">{submitError}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={mutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!valid || !isDirty || mutation.isPending}
          startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {mutation.isPending ? (isEdit ? 'Saving...' : 'Recording...') : isEdit ? 'Save Changes' : 'Record Payment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
